/* ============================================================
   component-collection.js
   Custom Element <edj-collection> — page collection EDJ
   Lot 4 Batch B — pixel-perfect EDJ

   Responsabilités :
   - View toggle (grid / list) — change data-view sur .edj-collection__grid,
     persiste choix en localStorage
   - Load-more via Shopify Section Rendering API
     (fetch ?section_id=<id>&page=N → extrait .edj-collection__cell, append)
   - Mobile dialog filtres + sort — open/close
   - Auto-submit du form filtres sur change (desktop only)
   - Sort dropdown : auto-redirige vers ?sort_by=…
   ============================================================ */

class EdjCollection extends HTMLElement {
  constructor() {
    super();
    this._onViewClick = this._onViewClick.bind(this);
    this._onSortChange = this._onSortChange.bind(this);
    this._onSortOptionClick = this._onSortOptionClick.bind(this);
    this._onOpenFilters = this._onOpenFilters.bind(this);
    this._onOpenSort = this._onOpenSort.bind(this);
    this._onCloseDialog = this._onCloseDialog.bind(this);
    this._onLoadMore = this._onLoadMore.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);
    this._onApplyFilters = this._onApplyFilters.bind(this);
    this._onDrawerAnchorClick = this._onDrawerAnchorClick.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.sectionUrl = this.dataset.sectionUrl;
    this.context = this.dataset.context || 'collection'; // 'collection' | 'search'
    this.searchQuery = this.dataset.query || '';
    this.grid = this.querySelector('[data-products-grid]');
    this.viewBtns = this.querySelectorAll('[data-view]');
    this.sortSelect = this.querySelector('[data-sort]');
    this.filterForm = this.querySelector('[data-filters-form]');
    this.loadMoreBtn = this.querySelector('[data-loadmore]');
    this.shownCountEl = this.querySelector('[data-shown-count]');
    this.progressBar = this.querySelector('[data-progress]');
    this.openFiltersBtn = this.querySelector('[data-open-filters]');
    this.openSortBtn = this.querySelector('[data-open-sort]');
    this.dialogFilters = this.querySelector('[data-dialog="filters"]');
    this.dialogSort = this.querySelector('[data-dialog="sort"]');
    this.applyBtn = this.querySelector('[data-apply-filters]');
    this.drawerForm = this.querySelector('.edj-coll-filters--drawer');

    this._initView();
    this._bindViewToggle();
    this._bindSort();
    this._bindFilterAutoSubmit();
    this._bindLoadMore();
    this._bindMobileDialogs();
    this._bindDrawerDeferredFilters();
    if (this.context === 'search' && this.searchQuery) {
      this._highlightSearchQuery(this);
    }
  }

  /* ─── HIGHLIGHT (mode search) ───────────────────────────── */
  _highlightSearchQuery(root) {
    const q = this.searchQuery;
    if (!q || q.length < 2) return;
    const titles = root.querySelectorAll('.edj-collection__cell .edj-coll-card__title');
    titles.forEach((node) => this._applyHighlight(node, q));
  }

  _applyHighlight(node, q) {
    if (!node) return;
    const text = node.textContent || '';
    if (!text) return;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return;
    node.textContent = '';
    node.appendChild(document.createTextNode(text.slice(0, i)));
    const mark = document.createElement('mark');
    mark.className = 'edj-coll-card__mark';
    mark.appendChild(document.createTextNode(text.slice(i, i + q.length)));
    node.appendChild(mark);
    node.appendChild(document.createTextNode(text.slice(i + q.length)));
  }

  /* ─── VIEW TOGGLE (grid / list) ─────────────────────────── */

  _initView() {
    let savedView = null;
    try { savedView = localStorage.getItem('edj-coll-view'); } catch (e) { /* no-op */ }
    const view = savedView === 'list' || savedView === 'grid'
      ? savedView
      : (this.dataset.defaultView || 'grid');
    this._setView(view);
  }

  _setView(view) {
    if (!this.grid) return;
    this.grid.setAttribute('data-view', view);
    this.viewBtns.forEach((b) => {
      const isActive = b.dataset.view === view;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-pressed', String(isActive));
    });
    try { localStorage.setItem('edj-coll-view', view); } catch (e) { /* no-op */ }
  }

  _bindViewToggle() {
    this.viewBtns.forEach((b) => b.addEventListener('click', this._onViewClick));
  }

  _onViewClick(event) {
    const btn = event.currentTarget;
    const view = btn.dataset.view;
    if (view) this._setView(view);
  }

  /* ─── SORT ──────────────────────────────────────────────── */

  _bindSort() {
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this._onSortChange);
    }
    this.querySelectorAll('[data-sort-option]').forEach((b) => {
      b.addEventListener('click', this._onSortOptionClick);
    });
  }

  _onSortChange(event) {
    const value = event.currentTarget.value;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  _onSortOptionClick(event) {
    const value = event.currentTarget.dataset.sortOption;
    if (!value) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  /* ─── FILTRES (auto-submit desktop) ─────────────────────── */

  _bindFilterAutoSubmit() {
    if (!this.filterForm) return;
    // Sur desktop, on submit dès qu'un filtre change.
    // Sur mobile, on reste en "submit explicite" via le bouton.
    const inputs = this.filterForm.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach((i) => i.addEventListener('change', this._onFilterChange));
    // Inputs prix : submit on blur (évite spam)
    const priceInputs = this.filterForm.querySelectorAll('input[type="number"]');
    priceInputs.forEach((i) => {
      i.addEventListener('change', this._onFilterChange);
    });

    // Item 10 — boutons tranche prix : remplit min/max et submit
    // (s'applique aussi aux boutons range dans le drawer mobile, qui ne submit pas
    //  mais marquent visuellement — cf. _onPriceRangeClick)
    const allForms = this.querySelectorAll('form[data-filters-form]');
    allForms.forEach((form) => {
      const rangeBtns = form.querySelectorAll('[data-edj-price-range]');
      rangeBtns.forEach((btn) => btn.addEventListener('click', (ev) => this._onPriceRangeClick(ev)));
    });
  }

  _onPriceRangeClick(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    // Détermine le form qui contient ce bouton (desktop sidebar OU drawer mobile)
    const form = btn.closest('form[data-filters-form]') || this.filterForm;
    if (!form) return;
    const minName = btn.dataset.minName;
    const maxName = btn.dataset.maxName;
    const minVal = btn.dataset.min || '';
    const maxVal = btn.dataset.max || '';
    const minInput = form.querySelector(`input[name="${minName}"]`);
    const maxInput = form.querySelector(`input[name="${maxName}"]`);
    if (minInput) minInput.value = minVal === '0' ? '' : minVal;
    if (maxInput) maxInput.value = maxVal;
    // Iter 2026-05-04 Batch 4 item 7 — En drawer mobile : on marque visuellement
    // mais on attend "Appliquer". En sidebar desktop : submit immédiat.
    if (form === this.drawerForm) {
      // Toggle visual is-active sur les boutons range du même groupe
      const group = btn.parentElement;
      if (group) {
        group.querySelectorAll('[data-edj-price-range]').forEach((b) => b.classList.remove('is-active'));
      }
      btn.classList.add('is-active');
      return;
    }
    form.submit();
  }

  _onFilterChange(event) {
    // Mobile : pas d'auto-submit
    if (window.matchMedia('(max-width: 767.98px)').matches) return;
    if (this.filterForm) this.filterForm.submit();
  }

  /* ─── LOAD MORE (Section Rendering API) ─────────────────── */

  _bindLoadMore() {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.addEventListener('click', this._onLoadMore);
  }

  async _onLoadMore(event) {
    event.preventDefault();
    if (!this.loadMoreBtn) return;
    const nextHref = this.loadMoreBtn.getAttribute('href');
    if (!nextHref) return;

    this.loadMoreBtn.setAttribute('aria-busy', 'true');
    const originalText = this.loadMoreBtn.textContent;
    this.loadMoreBtn.textContent = 'Chargement…';

    try {
      // Construit l'URL Section Rendering API : /collections/handle?section_id=X&page=N&filter…
      const nextUrl = new URL(nextHref, window.location.origin);
      const sectionUrl = new URL(window.location.pathname, window.location.origin);
      // Recopie tous les params de l'URL courante (filtres + sort) + page de nextHref
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((v, k) => sectionUrl.searchParams.set(k, v));
      sectionUrl.searchParams.set('page', nextUrl.searchParams.get('page') || '2');
      sectionUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(sectionUrl.toString(), {
        headers: { 'Accept': 'text/html' },
      });
      if (!response.ok) throw new Error('Bad status: ' + response.status);
      const html = await response.text();

      // Extrait le grid de la réponse et append les nouvelles cellules
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-products-grid]');
      const newCells = newGrid ? newGrid.querySelectorAll('[data-cell]') : [];
      const newLoadMore = doc.querySelector('[data-loadmore]');
      const newShown = doc.querySelector('[data-shown-count]');

      if (this.grid && newCells.length > 0) {
        newCells.forEach((cell) => {
          const clone = cell.cloneNode(true);
          this.grid.appendChild(clone);
          if (this.context === 'search' && this.searchQuery) {
            const title = clone.querySelector('.edj-coll-card__title');
            if (title) this._applyHighlight(title, this.searchQuery);
          }
        });
      }

      // Met à jour le bouton load-more (next page) ou le supprime si dernière page
      if (newLoadMore && this.loadMoreBtn) {
        this.loadMoreBtn.setAttribute('href', newLoadMore.getAttribute('href'));
        this.loadMoreBtn.textContent = newLoadMore.textContent;
      } else if (this.loadMoreBtn) {
        this.loadMoreBtn.remove();
        this.loadMoreBtn = null;
      }

      // Met à jour le compteur "X sur Y affichées"
      if (this.shownCountEl) {
        const renderedCount = this.grid.querySelectorAll('[data-cell]').length;
        this.shownCountEl.textContent = String(renderedCount);
        if (this.progressBar) {
          const total = parseInt(this.progressBar.parentElement
            ? (this.progressBar.dataset.total || '')
            : '', 10);
          // total non stocké → on lit depuis le markup pagination meta
          const totalEl = this.querySelector('.edj-collection__pagination-meta span:last-child');
          const totalVal = totalEl ? parseInt(totalEl.textContent, 10) : null;
          if (totalVal && totalVal > 0) {
            const pct = Math.min(100, (renderedCount * 100) / totalVal);
            this.progressBar.style.width = pct + '%';
          }
        }
      }
    } catch (err) {
      console.error('[edj-collection] load-more failed:', err);
      if (this.loadMoreBtn) {
        this.loadMoreBtn.removeAttribute('aria-busy');
        this.loadMoreBtn.textContent = originalText;
      }
      return;
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeAttribute('aria-busy');
    }
  }

  /* ─── MOBILE DIALOGS ────────────────────────────────────── */

  _bindMobileDialogs() {
    if (this.openFiltersBtn) this.openFiltersBtn.addEventListener('click', this._onOpenFilters);
    if (this.openSortBtn) this.openSortBtn.addEventListener('click', this._onOpenSort);

    this.querySelectorAll('[data-close-dialog]').forEach((b) => {
      b.addEventListener('click', this._onCloseDialog);
    });

    // Fermeture par clic sur backdrop
    [this.dialogFilters, this.dialogSort].forEach((d) => {
      if (!d) return;
      d.addEventListener('click', (e) => {
        if (e.target === d) d.close();
      });
    });
  }

  _onOpenFilters() {
    if (this.dialogFilters && typeof this.dialogFilters.showModal === 'function') {
      this.dialogFilters.showModal();
    }
  }

  _onOpenSort() {
    if (this.dialogSort && typeof this.dialogSort.showModal === 'function') {
      this.dialogSort.showModal();
    }
  }

  _onCloseDialog(event) {
    const dialog = event.currentTarget.closest('dialog');
    if (dialog) dialog.close();
  }

  /* ─── DRAWER MOBILE — VALIDATION DIFFÉRÉE (item 7) ──────────
     Iter 2026-05-04 Batch 4 :
     En mobile, les sélections de filtres dans le drawer doivent être
     cumulées en local et appliquées UNIQUEMENT au clic « Appliquer ».
     Comportement desktop (sidebar) inchangé.
     ─────────────────────────────────────────────────────────── */
  _bindDrawerDeferredFilters() {
    if (!this.drawerForm) return;
    // Empêche le submit auto via input change/keydown : c'est le bouton
    // "Appliquer" qui orchestrera la navigation.
    this.drawerForm.addEventListener('submit', (e) => {
      // Si soumis via Enter dans un input prix → on délègue au handler "Appliquer"
      e.preventDefault();
      this._onApplyFilters();
    });

    // Intercepte les clics sur les <a> swatches/sizes/checkbox-list dans le drawer.
    const anchors = this.drawerForm.querySelectorAll('a[href]');
    anchors.forEach((a) => a.addEventListener('click', this._onDrawerAnchorClick));

    // Bouton Appliquer
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', this._onApplyFilters);
    }
  }

  _onDrawerAnchorClick(event) {
    // Tous les <a> du drawer (swatches, sizes, list values) doivent être différés.
    event.preventDefault();
    const a = event.currentTarget;
    a.classList.toggle('is-active');
    // Marqueur visuel : pending (lu en CSS si on veut, optionnel).
    a.setAttribute('data-edj-pending', a.classList.contains('is-active') ? '1' : '0');
  }

  _onApplyFilters() {
    if (!this.drawerForm) {
      // Fallback : ferme juste la dialog
      if (this.dialogFilters) this.dialogFilters.close();
      return;
    }

    // Construction de l'URL finale :
    // 1. Base = chemin actuel (sans search params filter.*)
    // 2. + sort_by préservé (si présent)
    // 3. + checkboxes du form (state actuel)
    // 4. + buckets <a> avec is-active dans le drawer (param-name + param-values)
    // 5. + inputs prix (min/max)
    const url = new URL(window.location.href);
    // Reset : on retire tous les filter.* + page (la nouvelle URL repart en page 1)
    const keysToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) keysToDelete.push(key);
    });
    keysToDelete.forEach((k) => url.searchParams.delete(k));
    url.searchParams.delete('page');

    // 3. Checkboxes cochés (filter.v.*, filter.p.*, etc.)
    this.drawerForm.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
      if (!cb.name) return;
      url.searchParams.append(cb.name, cb.value);
    });

    // 4. Buckets actifs (swatches + sizes)
    const activeBuckets = this.drawerForm.querySelectorAll('a[data-param-name].is-active');
    activeBuckets.forEach((a) => {
      const name = a.dataset.paramName;
      const valuesStr = a.dataset.paramValues || '';
      if (!name) return;
      const values = valuesStr.split('|').filter(Boolean);
      values.forEach((v) => url.searchParams.append(name, v));
    });

    // 5. Inputs prix
    const priceInputs = this.drawerForm.querySelectorAll('input[type="number"]');
    priceInputs.forEach((p) => {
      if (p.name && p.value) url.searchParams.append(p.name, p.value);
    });

    // Naviguer
    if (this.dialogFilters) this.dialogFilters.close();
    window.location.href = url.toString();
  }
}

if (!customElements.get('edj-collection')) {
  customElements.define('edj-collection', EdjCollection);
}
